import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Gift } from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import { CreditsProvider } from './contexts/CreditsContext';
import { LangProvider } from './contexts/LangContext';
import { AppShell } from './components/layout/AppShell';
import { DrawShell } from './components/layout/DrawShell';
import { Spinner } from './components/ui/Spinner';
import { isDrawHost } from './lib/drawHost';
import { surfaceConfig } from './lib/surface';

// Auth + public pages
const Login = lazy(() => import('./pages/Login'));
const PublicDraw = lazy(() => import('./pages/PublicDraw'));
// The public draw record — no auth, served on both the draw host and go.pdpo.ai
const VerifyDraw = lazy(() => import('./pages/VerifyDraw'));

// Authenticated pages
const Home = lazy(() => import('./pages/Home'));
const Wizard = lazy(() => import('./pages/Wizard'));
const Documents = lazy(() => import('./pages/Documents'));
const DocumentDetail = lazy(() => import('./pages/DocumentDetail'));
const DocumentsHeld = lazy(() => import('./pages/DocumentsHeld'));
const Consent = lazy(() => import('./pages/Consent'));
const ConsentFormDetail = lazy(() => import('./pages/ConsentFormDetail'));
const Pipeline = lazy(() => import('./pages/Pipeline'));
const DealDetail = lazy(() => import('./pages/DealDetail'));
const Leads = lazy(() => import('./pages/Leads'));
const PipelineSettings = lazy(() => import('./pages/PipelineSettings'));
const PipelineReports = lazy(() => import('./pages/PipelineReports'));
const Financing = lazy(() => import('./pages/Financing'));
const Portal = lazy(() => import('./pages/Portal'));
const PortalHome = lazy(() => import('./pages/Portal').then((m) => ({ default: m.PortalHome })));
// A business's invitation to keep a person's details — public, answered by the person
const Invite = lazy(() => import('./pages/Invite'));
const ApplicationDetail = lazy(() => import('./pages/Financing').then((m) => ({ default: m.ApplicationDetail })));
const Contacts = lazy(() => import('./pages/Contacts'));
const ContactDetail = lazy(() => import('./pages/ContactDetail'));
const Draws = lazy(() => import('./pages/Draws'));
const DrawNew = lazy(() => import('./pages/DrawNew'));
const DrawDetail = lazy(() => import('./pages/DrawDetail'));
const DrawShow = lazy(() => import('./pages/DrawShow'));
const Inbox = lazy(() => import('./pages/Inbox'));
const InboxDetail = lazy(() => import('./pages/InboxDetail'));
const Breach = lazy(() => import('./pages/Breach'));
const BreachNew = lazy(() => import('./pages/BreachNew'));
const BreachDetail = lazy(() => import('./pages/BreachDetail'));
const Adviser = lazy(() => import('./pages/Adviser'));
const Settings = lazy(() => import('./pages/Settings'));
const Help = lazy(() => import('./pages/Help'));

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper">
      <Spinner size="lg" />
    </div>
  );
}

function NoDrawHere() {
  return (
    <DrawShell>
      <div className="flex flex-col items-center gap-3 pt-10 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gold-tint">
          <Gift className="h-7 w-7 text-gold" />
        </span>
        <h1 className="font-display text-xl font-bold text-ink">No draw here</h1>
        <p className="max-w-xs text-sm text-mist-500">
          Check the link on the poster — it should look like{' '}
          <span className="font-mono text-xs">draw.pdpo.ai/shop-name</span>. Tiada cabutan di
          sini — sila semak pautan anda.
        </p>
      </div>
    </DrawShell>
  );
}

function PublicDrawRouter() {
  return (
    <Routes>
      <Route path="/verify/:code" element={<VerifyDraw />} />
      <Route path="/verify/:code/:sequence" element={<VerifyDraw />} />
      <Route path="/:orgSlug" element={<PublicDraw />} />
      <Route path="/:orgSlug/:code" element={<PublicDraw />} />
      <Route path="*" element={<NoDrawHere />} />
    </Routes>
  );
}

/** Signed in, but no users row — accounts are provisioned by invitation. */
function NoAccountScreen() {
  const { user, signOut } = useAuth();
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-5">
      <div className="w-full max-w-sm rounded-card bg-white p-6 text-center shadow-card">
        <h1 className="font-display text-xl font-bold text-ink">Your account isn't set up yet</h1>
        <p className="mt-2 text-sm text-mist-500">
          You signed in as {user?.email}, but this account hasn't been linked to a business. Ask
          whoever manages PDPO Go for your business to add you, or email{' '}
          <a href="mailto:hello@pdpo.ai" className="font-semibold text-ink underline underline-offset-2">
            hello@pdpo.ai
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-5 text-sm font-semibold text-ink underline underline-offset-2"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

/**
 * A full-platform company that landed here.
 *
 * A navigation mistake, not a purchasing decision — so one line and a way back,
 * with none of the selling that the same wall does in the other direction.
 */
function WrongProductScreen() {
  const { signOut } = useAuth();
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-5">
      <div className="w-full max-w-sm rounded-card bg-white p-6 text-center shadow-card">
        <h1 className="font-display text-xl font-bold text-ink">You're on the full platform</h1>
        <p className="mt-2 text-sm text-mist-500">
          Your business uses PDPO Assist at{' '}
          <a
            href="https://app.pdpo.ai"
            className="font-semibold text-ink underline underline-offset-2"
          >
            app.pdpo.ai
          </a>
          . Go is the smaller product for businesses without a compliance team.
        </p>
        <a
          href="https://app.pdpo.ai"
          className="mt-5 inline-block rounded-chip bg-ink px-4 py-2 text-sm font-semibold text-paper"
        >
          Take me there
        </a>
        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-4 block w-full text-sm font-semibold text-mist-500 underline underline-offset-2"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

/** A main company without the Campaigns module, at the campaigns door. */
function CampaignsNotEnabledScreen() {
  const { signOut } = useAuth();
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-5">
      <div className="w-full max-w-sm rounded-card bg-white p-6 text-center shadow-card">
        <h1 className="font-display text-xl font-bold text-ink">
          Campaigns isn&rsquo;t switched on
        </h1>
        <p className="mt-2 text-sm text-mist-500">
          Your organisation uses PDPO Assist, but the Campaigns module isn&rsquo;t part of your
          subscription yet. Email{' '}
          <a
            href="mailto:hello@pdpo.ai?subject=Campaigns%20module"
            className="font-semibold text-ink underline underline-offset-2"
          >
            hello@pdpo.ai
          </a>{' '}
          to add it.
        </p>
        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-5 text-sm font-semibold text-ink underline underline-offset-2"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

/**
 * The module is on, but this person has not been delegated the authority.
 * Access is governed through the Manual of Authorities in PDPO Assist — the
 * whole point is that who may collect customer data is a recorded delegation,
 * so the screen says who can grant it rather than pretending it is a bug.
 */
function NoCampaignsAuthorityScreen() {
  const { user, signOut } = useAuth();
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-5">
      <div className="w-full max-w-sm rounded-card bg-white p-6 text-center shadow-card">
        <h1 className="font-display text-xl font-bold text-ink">
          You haven&rsquo;t been given access yet
        </h1>
        <p className="mt-2 text-sm text-mist-500">
          {user?.email} is signed in, but running campaigns needs an authority granted in PDPO
          Assist&rsquo;s Manual of Authorities. Ask your data protection officer to delegate it.
        </p>
        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-5 text-sm font-semibold text-ink underline underline-offset-2"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function ProtectedRoutes() {
  const { user, profile, organisation, loading, hasCampaignsAuthority } = useAuth();
  const config = surfaceConfig();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!profile) return <NoAccountScreen />;
  // Allowlist per door: go.pdpo.ai admits 'go' companies, campaigns.pdpo.ai
  // admits 'main' companies with the module. Anything else — a null, a typo, a
  // value nobody has thought of yet — is refused rather than admitted.
  if (organisation && organisation.surface !== config.organisationSurface) {
    return <WrongProductScreen />;
  }
  if (config.requiresAuthority && organisation) {
    if (!organisation.campaigns_enabled) return <CampaignsNotEnabledScreen />;
    if (hasCampaignsAuthority === undefined) return <LoadingScreen />;
    if (!hasCampaignsAuthority) return <NoCampaignsAuthorityScreen />;
  }

  return (
    <CreditsProvider>
      <Routes>
        {/* Full-screen presentation — outside AppShell, no chrome on stage */}
        <Route path="draws/:id/show" element={<DrawShow />} />
        <Route element={<AppShell />}>
          <Route index element={<Home />} />
          {surfaceConfig().wizard && <Route path="wizard" element={<Wizard />} />}
          {/* Generated documents and destruction are Assist's job on the
              campaigns door; the modules exist only where the config says so. */}
          {surfaceConfig().tabs.includes('documents') && (
            <>
              <Route path="documents" element={<Documents />} />
              <Route path="documents/:id" element={<DocumentDetail />} />
            </>
          )}
          {surfaceConfig().allowDestruction && (
            <Route path="documents/held" element={<DocumentsHeld />} />
          )}
          <Route path="consent" element={<Consent />} />
          <Route path="consent/forms/:id" element={<ConsentFormDetail />} />
          <Route path="contacts" element={<Contacts />} />
          {surfaceConfig().pipeline && <Route path="pipeline" element={<Pipeline />} />}
          {surfaceConfig().pipeline && <Route path="pipeline/settings" element={<PipelineSettings />} />}
          {surfaceConfig().pipeline && <Route path="pipeline/reports" element={<PipelineReports />} />}
          {surfaceConfig().pipeline && <Route path="pipeline/:id" element={<DealDetail />} />}
          {surfaceConfig().pipeline && <Route path="leads" element={<Leads />} />}
          {surfaceConfig().pipeline && <Route path="financing" element={<Financing />} />}
          {surfaceConfig().pipeline && <Route path="financing/:id" element={<ApplicationDetail />} />}
          <Route path="contacts/:id" element={<ContactDetail />} />
          <Route path="draws" element={<Draws />} />
          <Route path="draws/new" element={<DrawNew />} />
          <Route path="draws/:id" element={<DrawDetail />} />
          {surfaceConfig().tabs.includes('inbox') && (
            <>
              <Route path="inbox" element={<Inbox />} />
              <Route path="inbox/:id" element={<InboxDetail />} />
            </>
          )}
          {/* Report-only on campaigns: the red button still files an incident
              into the same breach_records the DPO works in Assist, but the
              management log lives there, not here. */}
          {surfaceConfig().breach === 'manage' && (
            <>
              <Route path="breach" element={<Breach />} />
              <Route path="breach/:id" element={<BreachDetail />} />
            </>
          )}
          <Route path="breach/new" element={<BreachNew />} />
          <Route path="adviser" element={<Adviser />} />
          <Route path="help" element={<Help />} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </CreditsProvider>
  );
}

export default function App() {
  // draw.pdpo.ai serves ONLY the public draw pages — never the app.
  if (isDrawHost(window.location.hostname)) {
    return (
      <BrowserRouter>
        <LangProvider>
          <Suspense fallback={<LoadingScreen />}>
            <PublicDrawRouter />
          </Suspense>
        </LangProvider>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <LangProvider>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/portal" element={<Portal />} />
            <Route path="/portal/home" element={<PortalHome />} />
            <Route path="/i/:token" element={<Invite />} />
            {/* Renders the same public draw page inside the app for testing */}
            <Route path="/draw-preview/:code" element={<PublicDraw />} />
            {/* The draw record is public here too — a QR scanned indoors may land on go.pdpo.ai */}
            <Route path="/verify/:code" element={<VerifyDraw />} />
            <Route path="/verify/:code/:sequence" element={<VerifyDraw />} />
            <Route path="/*" element={<ProtectedRoutes />} />
          </Routes>
        </Suspense>
      </LangProvider>
    </BrowserRouter>
  );
}
